/**
 * CharacterCounter — Apple-pattern character limit indicator.
 *
 * Only appears when approaching the limit (within 20% or 20 chars,
 * whichever is smaller). Turns warning color when ≤10 chars remain.
 * Hidden when plenty of space is left — reduces visual noise.
 *
 * Usage:
 *   <CharacterCounter value={text} max={100} />
 */

import { ds } from '../tokens';

interface CharacterCounterProps {
  value: string;
  max: number;
  className?: string;
  align?: 'left' | 'right';
}

export function CharacterCounter({
  value,
  max,
  className = '',
  align = 'right',
}: CharacterCounterProps) {
  const remaining = max - (value?.length || 0);
  const threshold = Math.min(20, Math.floor(max * 0.2));

  // Apple pattern: only show when approaching the limit
  if (remaining > threshold) return null;

  const isWarning = remaining <= 10;
  const alignClass = align === 'right' ? 'text-right' : 'text-left';
  const colorClass = isWarning ? ds.color.label.warning : ds.color.label.secondary;

  return (
    <div
      className={[ds.type.caption1, ds.type.weight.medium, 'mt-1', alignClass, colorClass, className].join(' ')}
      aria-live="polite"
    >
      {remaining} characters remaining
    </div>
  );
}
